// src/lib/mso-editor.ts
//
// ★★ ANNEX A 22 — the rule editor's decisions, as pure functions so a screen and a test read the
// same answer: why Activate is unavailable, which validator messages belong to which part of the
// definition, and what a freshly chosen condition starts as.
//
// ⚠ NOTHING HERE ENFORCES ANYTHING. Activation is refused by the route (RULE_NOT_TESTED,
// MAKER_CHECKER_VIOLATION) and by the database; this file only lets the screen SAY SO BEFORE the
// officer presses the button, so the refusal is not the first they hear of it. Hiding or disabling
// a control is presentation, exactly as `Shell.tsx` states for its navigation.
import { MSO_CONDITION_FIELDS } from './mso-vocabulary'
import type { MsoCondition, MsoRuleDefinition, MsoRuleVersion, MsoScalar, MsoTestRun } from './types'

export interface ActivationBlocker {
  /** A stable token a test (and a reader of the DOM) can key on. */
  readonly code: 'MAKER_CHECKER' | 'NOT_TESTED' | 'LAST_RUN_FAILED' | 'UNSAVED' | 'NO_NOTE'
  readonly reason: string
}

export const MAKER_CHECKER_REASON =
  'You created or last edited this draft, so a second PSD officer must activate it. The officer who authored a rule ' +
  'cannot be the one who puts it into force.'

export const NOT_TESTED_REASON =
  'No passing test run covers this exact definition. Run the tests after your last edit: editing a tested draft ' +
  'voids its test evidence, because the evidence is bound to the definition it was run against.'

/** The server's own sentence for a failed latest run (`RULE_NOT_TESTED`), shown before the press as well as after it. */
export const LAST_RUN_FAILED_REASON =
  'The latest test run on this definition failed, so it cannot be activated. Fix the definition or the failing cases and run the tests again.'

export const UNSAVED_REASON =
  'You have unsaved changes. Save the draft first; the tests and the activation both apply to the saved definition.'

export const NO_NOTE_REASON = 'Say where this rule’s figures came from before activating it.'

/**
 * Who is looking, as far as the author check can tell.
 *
 * ⚠ `GET /api/me` reports the SESSION id as `id`, not the user id the draft's `createdBy` and
 * `lastEditedBy` hold, so the two never match and Activate stayed enabled for the author (the server
 * refused only after the confirmation). The user id is used when the session reports one (`userId`);
 * otherwise the officer is recognised by DISPLAY NAME against the names the route attaches. A name is a
 * weaker key than an id (two officers can share one), which fails SAFE here: Activate is shown disabled with
 * its reason, and the route stays the authority either way.
 */
export interface Viewer {
  readonly userId?: string | null
  readonly displayName?: string | null
}

export function viewerAuthoredDraft(version: MsoRuleVersion, viewer: Viewer | null): boolean {
  if (viewer === null) return false
  const userId = viewer.userId ?? null
  if (userId !== null && (version.createdBy === userId || version.lastEditedBy === userId)) return true
  const name = (viewer.displayName ?? '').trim()
  if (name.length === 0) return false
  return [version.createdByName, version.lastEditedByName].some((n) => typeof n === 'string' && n.trim() === name)
}

/** True when a test run PASSED on exactly this version's current definition. */
export function hasPassingRunOnCurrentHash(version: MsoRuleVersion, runs: readonly MsoTestRun[]): boolean {
  return runs.some((run) => run.passed && run.definitionHash === version.definitionHash)
}

/**
 * Every reason Activate is unavailable right now, in the order an officer can act on them.
 * An empty list means the button is available. The viewer is `null` while the session is
 * unknown, which blocks nothing here: the route is the authority and refuses regardless.
 */
export function activationBlockers(input: {
  readonly version: MsoRuleVersion
  readonly viewer: Viewer | null
  readonly runs: readonly MsoTestRun[]
  readonly dirty: boolean
  readonly note: string
}): readonly ActivationBlocker[] {
  const { version, viewer, runs, dirty, note } = input
  const blockers: ActivationBlocker[] = []
  if (viewerAuthoredDraft(version, viewer)) {
    blockers.push({ code: 'MAKER_CHECKER', reason: MAKER_CHECKER_REASON })
  }
  if (dirty) blockers.push({ code: 'UNSAVED', reason: UNSAVED_REASON })
  else if (!hasPassingRunOnCurrentHash(version, runs)) {
    const latest = [...runs].filter((run) => run.definitionHash === version.definitionHash).sort((a, b) => (a.runAt < b.runAt ? 1 : -1))[0]
    if (latest !== undefined && !latest.passed) blockers.push({ code: 'LAST_RUN_FAILED', reason: LAST_RUN_FAILED_REASON })
    else blockers.push({ code: 'NOT_TESTED', reason: NOT_TESTED_REASON })
  }
  if (note.trim().length === 0) blockers.push({ code: 'NO_NOTE', reason: NO_NOTE_REASON })
  return blockers
}

/**
 * The validator's messages that belong to one part of the definition. The route names a part by a
 * leading path (`table[2].shares[0]: officerBasisPoints must be ...`), so a message belongs to
 * `table[2]` when it starts with that path followed by a boundary.
 */
export function errorsFor(errors: readonly string[], path: string): readonly string[] {
  return errors.filter((e) => e === path || e.startsWith(`${path}.`) || e.startsWith(`${path}[`) || e.startsWith(`${path}:`) || e.startsWith(`${path} `))
}

/** Messages that name no specific part of the editor, shown once at the top so none is lost. */
export function unplacedErrors(errors: readonly string[]): readonly string[] {
  return errors.filter((e) => !/^(table\[\d+\]|applicability\[\d+\]|precedence|rounding|requiredInputs|schemaVersion)/.test(e))
}

/** What a condition on `field` starts as when the officer picks it. */
export function defaultCondition(field: string): MsoCondition {
  const domain = MSO_CONDITION_FIELDS[field]
  if (Array.isArray(domain)) return { field, op: 'eq', value: domain[0] ?? '' }
  if (domain === 'boolean') return { field, op: 'eq', value: true }
  return { field, op: 'eq', value: '' }
}

/** A condition re-based on a new operator, carrying over whatever value still makes sense. */
export function withOperator(condition: MsoCondition, op: string): MsoCondition {
  if (op === 'present' || op === 'absent') return { field: condition.field, op }
  if (op === 'in') {
    const current = condition.value
    const list = Array.isArray(current) ? current : current === undefined || current === '' ? [] : [current as MsoScalar]
    return { field: condition.field, op, value: list as readonly MsoScalar[] }
  }
  // eq / neq take ONE value.
  const current = condition.value
  if (Array.isArray(current)) return { field: condition.field, op, value: (current as readonly MsoScalar[])[0] ?? defaultCondition(condition.field).value ?? '' }
  if (current === undefined) return { field: condition.field, op, value: defaultCondition(condition.field).value ?? '' }
  return { field: condition.field, op, value: current }
}

/** "A, B" typed into an `in` box for a free-text or boolean field, as the list the route takes. */
export function parseListText(field: string, text: string): readonly MsoScalar[] {
  const items = text
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
  return MSO_CONDITION_FIELDS[field] === 'boolean' ? items.map((t) => t.toLowerCase() === 'true') : items
}

/** "MSO-R4" is "R4": the short form a decision-row id carries ("R4-PRINCIPAL"). */
export function shortRuleKey(ruleKey: string): string {
  return ruleKey.replace(/^MSO-/, '')
}

/**
 * A copy of `definition` whose decision-row ids carry the NEW rule's key. A new scenario starts as a copy of a
 * rule that works, and its rows still read `R1-PRINCIPAL` under a rule called MSO-R4, which is a trace that
 * names the wrong rule. Only the source key's own prefix (`R1-`) is replaced, so a row id that never carried
 * it is left exactly as the officer would find it.
 */
export function rewriteRowIds(definition: MsoRuleDefinition, fromKey: string, toKey: string): MsoRuleDefinition {
  const from = `${shortRuleKey(fromKey)}-`
  const to = `${shortRuleKey(toKey)}-`
  if (from === to) return definition
  return {
    ...definition,
    table: definition.table.map((row) => (row.id.startsWith(from) ? { ...row, id: `${to}${row.id.slice(from.length)}` } : row)),
  }
}
